// src/components/EnemiesList.js
import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import AddEnemyForm from './AddEnemyForm';
import EnemyModal from './EnemyModal';
import { colors } from '../styles/colors';

const EnemiesList = () => {
  const [enemies, setEnemies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEnemy, setSelectedEnemy] = useState(null);

  const fetchEnemies = async () => {
    try {
      const { data, error } = await supabase
        .from('enemies')
        .select('*')
        .order('id');
      if (error) throw error;
      setEnemies(data);
    } catch (err) {
      console.error('Supabase fetch error:', err);
      setEnemies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnemies();

    const channel = supabase
      .channel('public:enemies')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'enemies' }, (payload) => {
        if (payload.eventType === 'INSERT') setEnemies((prev) => [...prev, payload.new]);
        if (payload.eventType === 'UPDATE')
          setEnemies((prev) => prev.map((e) => (e.id === payload.new.id ? payload.new : e)));
        if (payload.eventType === 'DELETE')
          setEnemies((prev) => prev.filter((e) => e.id !== payload.old.id));
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  const filteredEnemies = enemies.filter(
    (e) =>
      e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', padding: '1rem' }}>
      <div style={{ flex: '2 1 400px', minWidth: '300px' }}>
        <h2 style={{ color: colors.primary, marginBottom: '1rem' }}>Enemies</h2>

        <input
          type="text"
          placeholder="Search enemies..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '0.5rem 0.75rem',
            marginBottom: '1rem',
            borderRadius: '8px',
            border: `1px solid ${colors.primary}`,
            outline: 'none',
            fontSize: '0.95rem',
          }}
        />

        {loading ? (
          <p>Loading...</p>
        ) : filteredEnemies.length === 0 ? (
          <p>No enemies found.</p>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '1rem',
            }}
          >
            {filteredEnemies.map((e) => (
              <div
                key={e.id}
                onClick={() => setSelectedEnemy(e)}
                style={{
                  backgroundColor: colors.background,
                  padding: '1rem',
                  borderRadius: '12px',
                  border: `1px solid ${colors.primary}20`,
                  boxShadow: '0 8px 18px rgba(0,0,0,0.08)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(ev) => {
                  ev.currentTarget.style.transform = 'translateY(-4px)';
                  ev.currentTarget.style.boxShadow = '0 12px 25px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(ev) => {
                  ev.currentTarget.style.transform = 'translateY(0)';
                  ev.currentTarget.style.boxShadow = '0 8px 18px rgba(0,0,0,0.08)';
                }}
              >
                <h3 style={{ margin: '0 0 6px 0', color: colors.primary }}>👾 {e.name}</h3>
                <p style={{ margin: 0, fontWeight: '500' }}>{e.type}</p>
                <p style={{ marginTop: '4px', fontSize: '0.9rem', color: colors.textLight }}>
                  Game ID: {e.game_id || 'Unknown'}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ flex: '1 1 300px', minWidth: '280px' }}>
        <AddEnemyForm onEnemyAdded={(newEnemy) => setEnemies((prev) => [...prev, newEnemy])} />
      </div>

      <EnemyModal enemy={selectedEnemy} onClose={() => setSelectedEnemy(null)} />
    </div>
  );
};

export default EnemiesList;